import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiAlertTriangle, FiCheck, FiCrosshair, FiLink, FiRefreshCw, FiTruck } from 'react-icons/fi';
import api from '../../api/axios';
import { DashLang } from '../../utils/dashboardTranslations';

const copy = {
  en: {
    title: 'Riders Delivery',
    intro: 'Hand your delivery orders to Riders couriers. Riders picks the order up from your branch and delivers it to the customer.',
    notAvailable: 'Riders delivery is available only to stores created through Hadaq.',
    enable: 'Enable Riders delivery',
    blockers: 'Before you can send orders',
    ready: 'Everything is set up. Orders can be sent to Riders.',
    cash: 'Allow Riders for cash orders',
    cashHint: 'Riders drivers never collect money. Only turn this on if you collect payment from the customer yourself.',
    method: 'Shipping method',
    pricing: 'Delivery price shown to the customer',
    passthrough: 'Pass-through — customer pays exactly what Riders charges',
    markup: 'Markup — Riders price plus your uplift',
    fixed: 'Fixed — keep my own area fees and absorb the difference',
    markupType: 'Uplift type',
    markupFixed: 'Fixed amount',
    markupPct: 'Percentage',
    markupValue: 'Uplift',
    minCharge: 'Minimum charge',
    maxCharge: 'Maximum charge',
    bandHint: 'Optional. Keeps the delivery fee inside a predictable range even on long trips.',
    dispatch: 'Send orders to Riders',
    manual: 'Manually — I press the button on each order',
    onConfirm: 'When I confirm the order',
    onReady: 'When I mark the order ready',
    defaultBranch: 'Default pickup branch',
    branches: 'Branches',
    branchHint: 'Each branch must be linked to Riders with an exact Kuwait address before it can be used for pickup.',
    linked: 'Linked',
    notLinked: 'Not linked',
    link: 'Link to Riders',
    relink: 'Update on Riders',
    gov: 'Governorate',
    area: 'Area',
    block: 'Block',
    street: 'Street',
    avenue: 'Avenue',
    house: 'Building / House',
    coords: 'Branch location',
    useMap: 'Use my current location',
    locating: 'Getting location…',
    areasTitle: 'Delivery area matching',
    areasHint: 'Your delivery areas must be matched to Riders areas so orders can be routed. Matching is by name and can be re-run any time.',
    matchNow: 'Match areas automatically',
    matching: 'Matching…',
    mapped: 'matched',
    unmapped: 'not matched',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    selectGovFirst: 'Choose a governorate first',
    selectAreaFirst: 'Choose an area first',
    none: 'None',
    optional: 'optional',
  },
  ar: {
    title: 'توصيل رايدرز',
    intro: 'سلّم طلبات التوصيل لمندوبي رايدرز. يستلم المندوب الطلب من فرعك ويوصله للعميل.',
    notAvailable: 'توصيل رايدرز متاح فقط للمتاجر المنشأة عبر حداق.',
    enable: 'تفعيل توصيل رايدرز',
    blockers: 'قبل أن تتمكن من إرسال الطلبات',
    ready: 'كل شيء جاهز. يمكن إرسال الطلبات إلى رايدرز.',
    cash: 'السماح برايدرز لطلبات الدفع نقداً',
    cashHint: 'مندوبو رايدرز لا يستلمون أي مبالغ. فعّل هذا فقط إذا كنت تحصّل المبلغ من العميل بنفسك.',
    method: 'طريقة الشحن',
    pricing: 'سعر التوصيل المعروض للعميل',
    passthrough: 'تمرير — العميل يدفع تكلفة رايدرز كما هي',
    markup: 'هامش ربح — سعر رايدرز زائد الزيادة',
    fixed: 'ثابت — احتفظ برسوم مناطقي وتحمّل الفرق',
    markupType: 'نوع الزيادة',
    markupFixed: 'مبلغ ثابت',
    markupPct: 'نسبة مئوية',
    markupValue: 'الزيادة',
    minCharge: 'أقل رسوم',
    maxCharge: 'أقصى رسوم',
    bandHint: 'اختياري. يبقي رسوم التوصيل ضمن نطاق متوقع حتى في الرحلات الطويلة.',
    dispatch: 'إرسال الطلبات إلى رايدرز',
    manual: 'يدوياً — أضغط الزر لكل طلب',
    onConfirm: 'عند تأكيد الطلب',
    onReady: 'عند تجهيز الطلب',
    defaultBranch: 'فرع الاستلام الافتراضي',
    branches: 'الفروع',
    branchHint: 'يجب ربط كل فرع برايدرز بعنوان كويتي دقيق قبل استخدامه للاستلام.',
    linked: 'مرتبط',
    notLinked: 'غير مرتبط',
    link: 'ربط برايدرز',
    relink: 'تحديث في رايدرز',
    gov: 'المحافظة',
    area: 'المنطقة',
    block: 'القطعة',
    street: 'الشارع',
    avenue: 'الجادة',
    house: 'المبنى / المنزل',
    coords: 'موقع الفرع',
    useMap: 'استخدم موقعي الحالي',
    locating: 'جاري تحديد الموقع…',
    areasTitle: 'مطابقة مناطق التوصيل',
    areasHint: 'يجب مطابقة مناطق التوصيل لديك بمناطق رايدرز حتى تُوجَّه الطلبات. المطابقة بالاسم ويمكن إعادتها في أي وقت.',
    matchNow: 'مطابقة المناطق تلقائياً',
    matching: 'جاري المطابقة…',
    mapped: 'مطابقة',
    unmapped: 'غير مطابقة',
    save: 'حفظ',
    saving: 'جاري الحفظ…',
    saved: 'تم الحفظ',
    selectGovFirst: 'اختر المحافظة أولاً',
    selectAreaFirst: 'اختر المنطقة أولاً',
    none: 'بدون',
    optional: 'اختياري',
  },
};

export default function RidersDelivery() {
  const { store, lang } = useOutletContext<{ store: any; lang: DashLang }>();
  const t = copy[lang];

  const [data, setData] = useState<any>(null);
  const [settings, setSettings] = useState<any>({});
  const [methods, setMethods] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [matching, setMatching] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [editingBranch, setEditingBranch] = useState<number | null>(null);

  // `silent` refetches in the background. Every toggle on this page saves and
  // then reloads; flipping the full-page loading flag each time would unmount
  // the form, collapse any open branch row and lose the scroll position.
  const load = useCallback(async (silent = false) => {
    if (!store?.id) return;
    if (!silent) setLoading(true);
    try {
      const res = await api.get(`/stores/${store.id}/riders`);
      setData(res.data);
      setSettings(res.data.settings || {});
      try {
        const m = await api.get(`/stores/${store.id}/riders/shipping-methods`);
        setMethods(Array.isArray(m.data) ? m.data : []);
      } catch { /* methods are optional for rendering the page */ }
    } catch (err: any) {
      if (err.response?.status === 403) setForbidden(true);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [store?.id]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch: any) => {
    setSaving(true);
    try {
      const res = await api.put(`/stores/${store.id}/riders`, patch);
      setSettings(res.data);
      toast.success(t.saved);
      load(true);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const matchAreas = async () => {
    setMatching(true);
    try {
      const res = await api.post(`/stores/${store.id}/riders/areas/sync`);
      const { matched, total, unmatched } = res.data;
      toast.success(`${matched}/${total} ${t.mapped}`);
      if (unmatched?.length) {
        toast.warn(`${unmatched.length} ${t.unmapped}: ${unmatched.slice(0, 5).join(', ')}${unmatched.length > 5 ? '…' : ''}`, { autoClose: 8000 });
      }
      load(true);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    } finally {
      setMatching(false);
    }
  };

  if (loading) return <div style={{ padding: 40, color: 'var(--gray-400)' }}>…</div>;

  if (forbidden) {
    return (
      <div className="card" style={{ marginTop: 24, textAlign: 'center', padding: 40 }}>
        <FiTruck size={36} color="var(--gray-300)" />
        <p style={{ marginTop: 12, color: 'var(--gray-500)' }}>{t.notAvailable}</p>
      </div>
    );
  }

  const blockers: string[] = data?.blockers || [];

  return (
    <div>
      <div className="page-header"><h1>{t.title}</h1></div>
      <p style={{ color: 'var(--gray-500)', fontSize: 14, marginBottom: 20 }}>{t.intro}</p>

      {/* Readiness */}
      <div className="card" style={{ marginBottom: 24 }}>
        {blockers.length > 0 ? (
          <>
            <div className="card-header"><h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiAlertTriangle color="var(--warning, #d97706)" /> {t.blockers}
            </h2></div>
            <ul style={{ margin: 0, paddingInlineStart: 20, color: 'var(--gray-600)', fontSize: 14, lineHeight: 1.9 }}>
              {blockers.map((b) => <li key={b}>{b}</li>)}
            </ul>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--success, #16a34a)', fontWeight: 600 }}>
            <FiCheck /> {t.ready}
          </div>
        )}
      </div>

      {/* Main settings */}
      <div className="card" style={{ marginBottom: 24 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 16 }}>
          <input type="checkbox" checked={!!settings.is_enabled}
            onChange={(e) => save({ is_enabled: e.target.checked })} />
          <span style={{ fontWeight: 600 }}>{t.enable}</span>
        </label>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 6 }}>
          <input type="checkbox" checked={!!settings.allow_cash_orders} style={{ marginTop: 3 }}
            onChange={(e) => save({ allow_cash_orders: e.target.checked })} />
          <span>
            <span style={{ fontWeight: 500 }}>{t.cash}</span>
            <span style={{ display: 'block', fontSize: 12, color: 'var(--gray-400)' }}>{t.cashHint}</span>
          </span>
        </label>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 20 }}>
          <div className="form-group" style={{ flex: 1, minWidth: 200 }}>
            <label className="form-label">{t.method}</label>
            <select className="form-input" value={settings.shipping_method_id || ''}
              onChange={(e) => {
                const id = e.target.value ? Number(e.target.value) : null;
                const m = methods.find((x) => x.id === id);
                save({ shipping_method_id: id, shipping_method_name: m ? (lang === 'ar' ? (m.name_ar || m.name_en) : m.name_en) : null });
              }}>
              <option value="">{t.none}</option>
              {methods.map((m) => (
                <option key={m.id} value={m.id}>{lang === 'ar' ? (m.name_ar || m.name_en) : m.name_en}</option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ flex: 1, minWidth: 200 }}>
            <label className="form-label">{t.dispatch}</label>
            <select className="form-input" value={settings.dispatch_trigger || 'on_ready'}
              onChange={(e) => save({ dispatch_trigger: e.target.value })}>
              <option value="manual">{t.manual}</option>
              <option value="on_confirm">{t.onConfirm}</option>
              <option value="on_ready">{t.onReady}</option>
            </select>
          </div>

          <div className="form-group" style={{ flex: 1, minWidth: 200 }}>
            <label className="form-label">{t.defaultBranch}</label>
            <select className="form-input" value={settings.default_branch_id || ''}
              onChange={(e) => save({ default_branch_id: e.target.value ? Number(e.target.value) : null })}>
              <option value="">{t.none}</option>
              {(data?.branches || []).filter((b: any) => b.riders_branch_id).map((b: any) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header"><h2>{t.pricing}</h2></div>
        {([['passthrough', t.passthrough], ['markup', t.markup], ['fixed', t.fixed]] as const).map(([value, label]) => (
          <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: 14 }}>
            <input type="radio" name="pricing_mode" checked={(settings.pricing_mode || 'passthrough') === value}
              onChange={() => save({ pricing_mode: value })} />
            {label}
          </label>
        ))}

        {settings.pricing_mode === 'markup' && (
          <div style={{ display: 'flex', gap: 16, marginTop: 14, flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: '0 0 180px' }}>
              <label className="form-label">{t.markupType}</label>
              <select className="form-input" value={settings.markup_type || 'fixed'}
                onChange={(e) => save({ markup_type: e.target.value })}>
                <option value="fixed">{t.markupFixed}</option>
                <option value="percentage">{t.markupPct}</option>
              </select>
            </div>
            <div className="form-group" style={{ flex: '0 0 160px' }}>
              <label className="form-label">{t.markupValue}</label>
              <input type="number" step="0.001" min="0" className="form-input"
                defaultValue={settings.markup_value ?? 0}
                onBlur={(e) => save({ markup_value: Number(e.target.value) || 0 })} />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 16, marginTop: 14, flexWrap: 'wrap' }}>
          <div className="form-group" style={{ flex: '0 0 160px' }}>
            <label className="form-label">{t.minCharge} <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>({t.optional})</span></label>
            <input type="number" step="0.001" min="0" className="form-input"
              defaultValue={settings.min_charge ?? ''}
              onBlur={(e) => save({ min_charge: e.target.value === '' ? null : Number(e.target.value) })} />
          </div>
          <div className="form-group" style={{ flex: '0 0 160px' }}>
            <label className="form-label">{t.maxCharge} <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>({t.optional})</span></label>
            <input type="number" step="0.001" min="0" className="form-input"
              defaultValue={settings.max_charge ?? ''}
              onBlur={(e) => save({ max_charge: e.target.value === '' ? null : Number(e.target.value) })} />
          </div>
        </div>
        <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>{t.bandHint}</p>
      </div>

      {/* Areas */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>{t.areasTitle}</h2>
          <button className="btn btn-white btn-sm" onClick={matchAreas} disabled={matching}>
            <FiRefreshCw size={14} /> {matching ? t.matching : t.matchNow}
          </button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 12 }}>{t.areasHint}</p>
        <div style={{ display: 'flex', gap: 20, fontSize: 14 }}>
          <span><strong style={{ color: 'var(--success, #16a34a)' }}>{data?.areas?.mapped ?? 0}</strong> {t.mapped}</span>
          <span><strong style={{ color: (data?.areas?.unmapped ?? 0) > 0 ? 'var(--danger)' : 'var(--gray-400)' }}>{data?.areas?.unmapped ?? 0}</strong> {t.unmapped}</span>
        </div>
      </div>

      {/* Branches */}
      <div className="card">
        <div className="card-header"><h2>{t.branches}</h2></div>
        <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 14 }}>{t.branchHint}</p>
        {(data?.branches || []).map((b: any) => (
          <BranchRow
            key={b.id}
            branch={b}
            storeId={store.id}
            t={t}
            open={editingBranch === b.id}
            onToggle={() => setEditingBranch(editingBranch === b.id ? null : b.id)}
            onSaved={() => load(true)}
          />
        ))}
        {(data?.branches || []).length === 0 && (
          <p style={{ color: 'var(--gray-400)', fontSize: 14 }}>—</p>
        )}
      </div>
    </div>
  );
}

/** One branch row with the Riders address form. */
function BranchRow({ branch, storeId, t, open, onToggle, onSaved }: any) {
  const [form, setForm] = useState<any>({
    riders_governorate_id: branch.riders_governorate_id || '',
    riders_area_id: branch.riders_area_id || '',
    riders_block_id: branch.riders_block_id || '',
    riders_street_id: branch.riders_street_id || '',
    avenue: branch.avenue || '',
    house: branch.house || '',
    latitude: branch.latitude || '',
    longitude: branch.longitude || '',
  });
  const [govs, setGovs] = useState<any[]>([]);
  const [areas, setAreas] = useState<any[]>([]);
  const [blocks, setBlocks] = useState<any[]>([]);
  const [streets, setStreets] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);

  const geo = useCallback(async (resource: string, params: any = {}) => {
    const res = await api.get(`/stores/${storeId}/riders/geo/${resource}`, { params });
    return Array.isArray(res.data) ? res.data : [];
  }, [storeId]);

  useEffect(() => { if (open) geo('governorates').then(setGovs).catch(() => {}); }, [open, geo]);
  useEffect(() => { if (form.riders_governorate_id) geo('areas', { governorate_id: form.riders_governorate_id }).then(setAreas).catch(() => {}); }, [form.riders_governorate_id, geo]);
  useEffect(() => { if (form.riders_area_id) geo('blocks', { area_id: form.riders_area_id }).then(setBlocks).catch(() => {}); }, [form.riders_area_id, geo]);
  useEffect(() => { if (form.riders_block_id) geo('streets', { block_id: form.riders_block_id }).then(setStreets).catch(() => {}); }, [form.riders_block_id, geo]);

  const set = (k: string, v: any) => setForm((f: any) => {
    const next = { ...f, [k]: v };
    // Clear the narrower parts of the address when a broader one changes.
    if (k === 'riders_governorate_id') { next.riders_area_id = ''; next.riders_block_id = ''; next.riders_street_id = ''; }
    if (k === 'riders_area_id') { next.riders_block_id = ''; next.riders_street_id = ''; }
    if (k === 'riders_block_id') { next.riders_street_id = ''; }
    return next;
  });

  /** Drop a pin from the browser and let Riders name the location. */
  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      setForm((f: any) => ({ ...f, latitude: latitude.toFixed(7), longitude: longitude.toFixed(7) }));
      try {
        const res = await api.get(`/stores/${storeId}/riders/geo/reverse`, { params: { lat: latitude, lng: longitude } });
        const d = res.data || {};
        if (d.gov?.id) {
          setForm((f: any) => ({
            ...f,
            riders_governorate_id: d.gov.id,
            riders_area_id: d.area?.id || '',
            riders_block_id: d.block?.id || '',
            riders_street_id: d.street?.id || '',
          }));
        }
      } catch { /* the pin alone is still useful */ }
      setLocating(false);
    }, () => { setLocating(false); toast.error('Location unavailable'); }, { timeout: 8000 });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/stores/${storeId}/riders/branches/${branch.id}/sync`, {
        ...form,
        riders_block_id: form.riders_block_id || null,
        riders_street_id: form.riders_street_id || null,
      });
      toast.success(res.data.message);
      onSaved();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ border: '1px solid var(--gray-100)', borderRadius: 8, marginBottom: 10 }}>
      <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, cursor: 'pointer' }}>
        <FiTruck size={15} color="var(--gray-400)" />
        <strong style={{ flex: 1, fontSize: 14 }}>{branch.name}</strong>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999,
          background: branch.riders_branch_id ? 'rgba(22,163,74,0.1)' : 'var(--gray-100)',
          color: branch.riders_branch_id ? 'var(--success, #16a34a)' : 'var(--gray-500)',
        }}>
          {branch.riders_branch_id ? t.linked : t.notLinked}
        </span>
      </div>

      {open && (
        <div style={{ padding: '0 12px 14px' }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Select label={t.gov} value={form.riders_governorate_id} onChange={(v: any) => set('riders_governorate_id', v)} options={govs} />
            <Select label={t.area} value={form.riders_area_id} onChange={(v: any) => set('riders_area_id', v)} options={areas} disabled={!form.riders_governorate_id} placeholder={t.selectGovFirst} />
            <Select label={`${t.block} (${t.optional})`} value={form.riders_block_id} onChange={(v: any) => set('riders_block_id', v)} options={blocks} disabled={!form.riders_area_id} placeholder={t.selectAreaFirst} />
            <Select label={`${t.street} (${t.optional})`} value={form.riders_street_id} onChange={(v: any) => set('riders_street_id', v)} options={streets} disabled={!form.riders_block_id} />
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
            <div className="form-group" style={{ flex: '0 0 130px' }}>
              <label className="form-label">{t.avenue}</label>
              <input className="form-input" value={form.avenue} onChange={(e) => set('avenue', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: '0 0 150px' }}>
              <label className="form-label">{t.house}</label>
              <input className="form-input" value={form.house} onChange={(e) => set('house', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: 240 }}>
              <label className="form-label">{t.coords}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="form-input" placeholder="lat" value={form.latitude} onChange={(e) => set('latitude', e.target.value)} />
                <input className="form-input" placeholder="lng" value={form.longitude} onChange={(e) => set('longitude', e.target.value)} />
                <button type="button" className="btn btn-white btn-sm" onClick={useMyLocation} disabled={locating} style={{ whiteSpace: 'nowrap' }}>
                  <FiCrosshair size={14} /> {locating ? t.locating : t.useMap}
                </button>
              </div>
            </div>
          </div>

          <button className="btn btn-primary btn-sm" onClick={submit} disabled={busy} style={{ marginTop: 6 }}>
            <FiLink size={14} /> {busy ? t.saving : (branch.riders_branch_id ? t.relink : t.link)}
          </button>
        </div>
      )}
    </div>
  );
}

function Select({ label, value, onChange, options, disabled, placeholder }: any) {
  return (
    <div className="form-group" style={{ flex: '1 1 170px', minWidth: 150 }}>
      <label className="form-label">{label}</label>
      <select className="form-input" value={value || ''} disabled={disabled}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}>
        <option value="">{disabled ? (placeholder || '—') : '—'}</option>
        {options.map((o: any) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    </div>
  );
}
